import React from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Form } from '@dashboard/components/Formik';
import Button from '@dashboard/components/Button';
import Link from '@dashboard/components/Link';
import Dialog from '@dashboard/components/Dialog';
import usePage from '@dashboard/hooks/usePage';
import { useAuth } from '@dashboard/context/AuthContext/AuthContext';
import { Alert, AlertAction, AlertDescription, AlertTitle } from '@dashboard/components/ui/alert';
import { InformationCircleIcon } from '@heroicons/react/24/outline';
import SettingsDivider from '@dashboard/components/SettingsDivider';
import RadioCard from '@dashboard/components/Form/components/RadioCard';
import getStripeDashboardUrl from '@dashboard/lib/stripe/get-stripe-dashboard-url';
import { PAGE_TYPES } from '@dashboard/enums/page-types';
import useProduct from '@/dashboard/scenes/PageEdit/scenes/PageProduct/useProduct';
import { fetcher } from '@dashboard/services/request';
import CreateTaxRateForm from './CreateTaxRateForm';

function getCurrentFixedTaxRates(page, product) {
  if (page?.type === PAGE_TYPES.EVENT) {
    const fromEvent = page?.eventDetails?.fixedTaxRates ?? [];
    if (fromEvent.length > 0) return fromEvent;
    return page?.tax?.fixedTaxRates ?? [];
  }
  const fromProduct = product?.fixedTaxRates ?? [];
  if (fromProduct.length > 0) return fromProduct;
  return page?.tax?.fixedTaxRates ?? [];
}

function getPageTax(page, product, seller, taxRates) {
  const fixedTaxRates = getCurrentFixedTaxRates(page, product);

  if (page?.tax) return { ...page.tax, fixedTaxRates };

  // Fallback for sellers whose `mode` field hasn't been written yet (pre-migration).
  // Mirrors the seller migration: legacy `tax.enabled === true` means Stripe Tax, otherwise no tax.
  const sellerMode = seller?.tax?.mode ?? (seller?.tax?.enabled === true ? 'stripe' : 'none');

  if (sellerMode === 'none') {
    return { enabled: false, mode: 'none', fixedTaxRates: [] };
  }

  if (sellerMode === 'fixed') {
    const defaultRate = taxRates?.find(rate => rate.default);
    return {
      enabled: true,
      mode: 'fixed',
      fixedTaxRates: defaultRate ? [defaultRate.id] : [],
    };
  }

  return { enabled: true, mode: 'stripe', fixedTaxRates: [] };
}

function buildPageTaxPayload(mode) {
  return mode === 'none' ? { enabled: false, mode: 'none' } : { enabled: true, mode };
}

const TAX_MODE_OPTIONS = [
  { value: 'none', label: 'No tax', helpText: 'No tax is charged on this page.' },
  {
    value: 'fixed',
    label: 'Fixed tax rates',
    helpText: 'Charge the same tax rate to every customer.',
  },
  {
    value: 'stripe',
    label: 'Stripe Tax',
    helpText: 'Automatically calculates tax based on your customer’s location.',
  },
];

const TAX_BEHAVIOR_OPTIONS = [
  {
    value: '',
    label: 'Use Stripe account setting',
    labelDescription: 'Tax behavior follows your Stripe account defaults.',
  },
  {
    value: 'inclusive',
    label: 'Inclusive',
    labelDescription: 'Tax is included in the price. Customers see one combined total.',
  },
  {
    value: 'exclusive',
    label: 'Exclusive',
    labelDescription: 'Tax is added on top of the price and shown separately at checkout.',
  },
  {
    value: 'automatic',
    label: 'Automatic',
    labelDescription: "Stripe determines inclusive or exclusive based on the customer's location.",
  },
];

const TAX_CODE_OPTIONS = [
  { value: '', label: 'Use Stripe account setting' },
  { value: 'txcd_99999999', label: 'General - Tangible Goods' },
  { value: 'txcd_20030000', label: 'General - Services' },
  { value: 'txcd_10000000', label: 'General - Electronically Supplied Services' },
];

const SettingsPageTax = () => {
  const { page, updatePage, refetch } = usePage();
  const { product, updateProduct } = useProduct();
  const { seller, token } = useAuth();
  const queryClient = useQueryClient();

  const isEventPage = page.type === PAGE_TYPES.EVENT;
  // A Stripe-managed plan's Price is owned externally, so the app can't sync
  // taxBehavior/taxCode onto it — the in-app controls would be inert. Show the
  // read-only "managed in Stripe" copy for these instead (see showStripeSettings).
  // TODO(M14): reads legacy product scalars — repoint to prices[]/selected price before legacy fields are removed (product.planManagedInStripe)
  const isPlanManagedSubscription = product?.planManagedInStripe === true;

  const [isCreateDialogOpen, setIsCreateDialogOpen] = React.useState(false);
  const [modeError, setModeError] = React.useState('');
  const [optimisticMode, setOptimisticMode] = React.useState(null);

  const { data: taxRates = [], isLoading: isLoadingTaxRates } = useQuery({
    queryKey: ['seller-tax-rates', seller?._id],
    queryFn: () => fetcher(`/sellers/${seller._id}/tax-rates`, token),
    enabled: !!seller?._id && !!token,
  });

  const currentTax = getPageTax(page, product, seller, taxRates);
  const currentMode = currentTax.enabled ? currentTax.mode || 'stripe' : 'none';
  const displayedMode = optimisticMode ?? currentMode;

  React.useEffect(() => {
    if (optimisticMode && optimisticMode === currentMode) {
      setOptimisticMode(null);
    }
  }, [currentMode, optimisticMode]);

  const rawFixedTaxRate = currentTax.fixedTaxRates?.[0];
  const selectedFixedTaxRateId = !rawFixedTaxRate
    ? ''
    : typeof rawFixedTaxRate === 'string'
      ? rawFixedTaxRate
      : rawFixedTaxRate._id || '';

  const currentTaxBehavior = isEventPage
    ? page.eventDetails?.taxBehavior || ''
    : product?.taxBehavior || '';

  const currentTaxCode = isEventPage ? page.eventDetails?.taxCode || '' : product?.taxCode || '';

  const createTaxRate = useMutation({
    mutationFn: values =>
      fetcher(`/sellers/${seller._id}/tax-rates`, token, {
        method: 'POST',
        body: JSON.stringify(values),
      }),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['seller-tax-rates', seller._id] }),
  });

  const applyTaxRateChange = async fixedTaxRateId => {
    const currentIds = (currentTax?.fixedTaxRates ?? []).map(r =>
      typeof r === 'string' ? r : r._id || undefined,
    );
    const fixedTaxRateIds = fixedTaxRateId ? [fixedTaxRateId] : currentIds;

    if (page.type === PAGE_TYPES.EVENT) {
      await updatePage.mutateAsync({
        eventDetails: { ...page.eventDetails, fixedTaxRates: fixedTaxRateIds },
      });
    } else {
      await updateProduct.mutateAsync({ fixedTaxRates: fixedTaxRateIds });
    }
  };

  const handleCreateTaxRate = async (values, { resetForm } = {}) => {
    const taxRate = await createTaxRate.mutateAsync(values);
    await updatePage.mutateAsync({ tax: buildPageTaxPayload('fixed') });
    await applyTaxRateChange(taxRate.id);
    refetch();
    resetForm?.();
  };

  const handleModeChange = async mode => {
    setOptimisticMode(mode);
    setModeError('');
    try {
      let fixedTaxRateId;
      if (mode === 'fixed' && !selectedFixedTaxRateId) {
        const defaultRate = taxRates.find(r => r.default);
        if (defaultRate) fixedTaxRateId = defaultRate.id;
      }
      await updatePage.mutateAsync({ tax: buildPageTaxPayload(mode) });
      if (mode === 'fixed' && fixedTaxRateId) {
        await applyTaxRateChange(fixedTaxRateId);
      }
      refetch();
    } catch (err) {
      setOptimisticMode(null);
      setModeError(err?.message || 'Unable to save tax settings.');
    }
  };

  const emptyPlaceholder = isLoadingTaxRates
    ? 'Loading tax rates...'
    : taxRates.length === 0
      ? 'Create a tax rate first'
      : 'Choose a fixed tax rate';

  const fixedTaxRateOptions = [
    { value: '', label: emptyPlaceholder, disabled: isLoadingTaxRates },
    ...taxRates.map(taxRate => ({
      value: taxRate.id,
      label: `${taxRate.displayName} (${taxRate.percentage}% ${taxRate.inclusive ? 'inclusive' : 'exclusive'})`,
    })),
  ];

  const showStripeEnableCallout = currentMode === 'stripe' && !seller.tax?.enabled;
  // Show the in-app tax code + behaviour controls whenever Stripe Tax is the
  // active mode. They govern one-time charges AND subscriptions: the product's
  // taxBehavior/taxCode is synced to the subscription's Stripe Price + Product,
  // which subscription automatic_tax reads. The one exception is a Stripe-managed
  // plan, whose Price the app doesn't own and can't sync — hide the controls
  // there so we don't show settings that silently never apply.
  const showStripeSettings =
    currentMode === 'stripe' && seller.tax?.enabled && !isPlanManagedSubscription;

  return (
    <div>
      <RadioCard.Group name="tax-mode" isInline={false}>
        {TAX_MODE_OPTIONS.map(option => (
          <RadioCard.Card
            key={option.value}
            name="tax-mode"
            value={option.value}
            checked={displayedMode === option.value}
            onChange={() => handleModeChange(option.value)}
          >
            <RadioCard.Label>{option.label}</RadioCard.Label>
            <RadioCard.LabelDescription>{option.helpText}</RadioCard.LabelDescription>
          </RadioCard.Card>
        ))}
      </RadioCard.Group>

      {modeError && <p className="mt-3 text-sm text-red-600 dark:text-red-400">{modeError}</p>}

      {currentMode === 'fixed' && (
        <>
          <SettingsDivider />
          <div className="space-y-4">
            <div>
              <h2 className="text-base font-semibold mb-0.5">Fixed tax rates</h2>
              <p className="text-sm text-gray-500 dark:text-muted-foreground">
                Create and manage fixed tax rates for your checkouts and events.
              </p>
            </div>

            {!isLoadingTaxRates && taxRates.length === 0 ? (
              <CreateTaxRateForm
                inline
                isPending={createTaxRate.isPending}
                onSubmit={handleCreateTaxRate}
              />
            ) : (
              <>
                {/* Remount once the rates load, so the loading-state select cannot autosave an unchanged rate. */}
                <Form
                  key={isLoadingTaxRates ? 'loading' : 'loaded'}
                  autosave
                  autosaveDebounceMs={0}
                  showState={false}
                  enableReinitialize
                  initialValues={{ fixedTaxRateId: selectedFixedTaxRateId }}
                  fields={[
                    {
                      name: 'fixedTaxRateId',
                      type: 'select',
                      label: 'Tax rate',
                      options: fixedTaxRateOptions,
                      disabled: isLoadingTaxRates,
                    },
                  ]}
                  onSubmit={async values => {
                    try {
                      await applyTaxRateChange(values.fixedTaxRateId);
                      refetch();
                      return { data: true };
                    } catch (err) {
                      return { error: err };
                    }
                  }}
                />

                <Button appearance="secondary" onClick={() => setIsCreateDialogOpen(true)}>
                  + Create
                </Button>

                <Dialog
                  title="Create tax rate"
                  description="Fixed tax rates apply the same rate to every customer, regardless of location."
                  size="md"
                  open={isCreateDialogOpen}
                  onCancel={() => setIsCreateDialogOpen(false)}
                  cancelButton={null}
                  confirmButton={null}
                >
                  {({ close }) => (
                    <CreateTaxRateForm
                      isPending={createTaxRate.isPending}
                      onSubmit={(values, { resetForm }) =>
                        handleCreateTaxRate(values, {
                          resetForm: () => {
                            resetForm();
                            close();
                          },
                        })
                      }
                    />
                  )}
                </Dialog>
              </>
            )}
          </div>
        </>
      )}

      {showStripeEnableCallout && (
        <>
          <SettingsDivider />
          <Alert variant="default" className="my-4">
            <InformationCircleIcon />
            <AlertTitle>Stripe Tax is not yet enabled</AlertTitle>
            <AlertDescription>
              Enable Stripe Tax to automatically calculate the right tax for every customer.
            </AlertDescription>
            <AlertAction>
              <Button to="/settings/tax/stripe-tax" appearance="secondary" size="small">
                Enable Stripe Tax
              </Button>
            </AlertAction>
          </Alert>
        </>
      )}

      {currentMode === 'stripe' && seller.tax?.enabled && (
        <>
          <SettingsDivider />
          <div className="space-y-6">
            <div>
              <h2 className="text-base font-semibold mb-0.5">Stripe Tax</h2>
              {isPlanManagedSubscription ? (
                <p className="text-sm text-gray-500 dark:text-muted-foreground">
                  Tax code and behavior for this Stripe-managed plan are controlled in your{' '}
                  <Link to={getStripeDashboardUrl('/settings/tax', { seller })} target="_blank">
                    Stripe Tax settings
                  </Link>
                  . Stripe handles tax calculation and invoicing automatically.
                </p>
              ) : (
                <p className="text-sm text-gray-500 dark:text-muted-foreground">
                  Tax is automatically calculated and collected based on your customer’s location.
                </p>
              )}
            </div>

            {showStripeSettings && (
              <Form
                autosave
                autosaveDebounceMs={0}
                showState={false}
                enableReinitialize
                initialValues={{ taxBehavior: currentTaxBehavior, taxCode: currentTaxCode }}
                fields={[
                  {
                    name: 'taxBehavior',
                    type: 'radioCard',
                    columns: 1,
                    label: 'Tax behavior',
                    labelDescription: (
                      <span className="block pb-2">
                        Whether the price includes or excludes tax. Defaults to your{' '}
                        <Link
                          to={getStripeDashboardUrl('/settings/tax', { seller })}
                          target="_blank"
                        >
                          Stripe Tax settings
                        </Link>
                        .
                      </span>
                    ),
                    options: TAX_BEHAVIOR_OPTIONS,
                  },
                  {
                    name: 'taxCode',
                    type: 'select',
                    label: 'Tax code',
                    options: TAX_CODE_OPTIONS,
                    helpText: (
                      <span>
                        The product tax code used when calculating tax. Defaults to your{' '}
                        <Link
                          to={getStripeDashboardUrl('/settings/tax', { seller })}
                          target="_blank"
                        >
                          Stripe Tax settings
                        </Link>
                        .
                      </span>
                    ),
                  },
                ]}
                onSubmit={async values => {
                  try {
                    if (isEventPage) {
                      await updatePage.mutateAsync({
                        eventDetails: {
                          ...page.eventDetails,
                          taxBehavior: values.taxBehavior,
                          taxCode: values.taxCode,
                        },
                      });
                    } else {
                      await updateProduct.mutateAsync({
                        taxBehavior: values.taxBehavior,
                        taxCode: values.taxCode,
                      });
                    }
                    refetch();
                    return { data: true };
                  } catch (err) {
                    return { error: err };
                  }
                }}
              />
            )}
          </div>
        </>
      )}
    </div>
  );
};

export default SettingsPageTax;
